// 🎯 CONFIGURATION: Company-specific performance criteria configuration
// Purpose: Allow companies to set their own speed and performance thresholds
// Approach: Simple, flexible configuration with multiple input types

import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface CompanyConfig {
  // 🚀 OVERALL SPEED & SCORE THRESHOLDS
  overallSpeedExpectation: number // Scale 1-10
  minimumAcceptableScore: number // 0-100
  
  // 🎯 RESPONSE TIME PREFERENCES
  maxResponseTimeHours: number // Number input
  urgentResponseTimeMinutes: number // Number input
  
  // 🌟 COMMUNICATION PREFERENCES  
  communicationStyle: 'formal' | 'casual' | 'mixed' // Dropdown
  prioritizeAsyncWork: boolean // Toggle
  
  // 📊 PERFORMANCE EVALUATION
  evaluationFrequency: 'daily' | 'weekly' | 'monthly' | 'quarterly' // Dropdown
  performanceWeights: {
    answerQuality: number // Scale 1-5
    responsiveness: number // Scale 1-5
    communicationBalance: number // Scale 1-5
    asyncDiscipline: number // Scale 1-5
  }
  
  // 🎯 INDUSTRY CONTEXT
  industryType: 'tech' | 'finance' | 'healthcare' | 'education' | 'consulting' | 'other' // Dropdown
  teamSize: 'small' | 'medium' | 'large' | 'enterprise' // Dropdown
  workingHoursFlexible: boolean // Toggle
}

export interface ConfigState {
  config: CompanyConfig
  isModified: boolean
  lastSaved: string | null
  errors: Record<string, string>
}

// 🌟 DEFAULT CONFIGURATION: Reasonable defaults for most companies
const defaultConfig: CompanyConfig = {
  overallSpeedExpectation: 7, // Scale 1-10, default to "good speed"
  minimumAcceptableScore: 60, // 60% as baseline
  
  maxResponseTimeHours: 24, // 1 day max response
  urgentResponseTimeMinutes: 60, // 1 hour for urgent
  
  communicationStyle: 'mixed',
  prioritizeAsyncWork: true,
  
  evaluationFrequency: 'weekly',
  performanceWeights: {
    answerQuality: 4, // High importance
    responsiveness: 3, // Medium-high importance  
    communicationBalance: 3, // Medium-high importance
    asyncDiscipline: 2 // Medium importance
  },
  
  industryType: 'tech',
  teamSize: 'medium',
  workingHoursFlexible: true
}

const initialState: ConfigState = {
  config: defaultConfig,
  isModified: false,
  lastSaved: null,
  errors: {}
}

const configSlice = createSlice({
  name: 'config',
  initialState,
  reducers: {
    // 🚀 UPDATE: Individual config field updates
    updateOverallSpeed: (state, action: PayloadAction<number>) => {
      state.config.overallSpeedExpectation = action.payload
      state.isModified = true
      delete state.errors.overallSpeedExpectation
    },
    
    updateMinimumScore: (state, action: PayloadAction<number>) => {
      state.config.minimumAcceptableScore = action.payload
      state.isModified = true
      delete state.errors.minimumAcceptableScore
    },
    
    updateResponseTime: (state, action: PayloadAction<{ type: 'max' | 'urgent'; value: number }>) => {
      if (action.payload.type === 'max') {
        state.config.maxResponseTimeHours = action.payload.value
        delete state.errors.maxResponseTimeHours
      } else {
        state.config.urgentResponseTimeMinutes = action.payload.value
        delete state.errors.urgentResponseTimeMinutes
      }
      state.isModified = true
    },
    
    updateCommunicationStyle: (state, action: PayloadAction<CompanyConfig['communicationStyle']>) => {
      state.config.communicationStyle = action.payload
      state.isModified = true
    },
    
    toggleAsyncPriority: (state) => {
      state.config.prioritizeAsyncWork = !state.config.prioritizeAsyncWork
      state.isModified = true
    },
    
    updateEvaluationFrequency: (state, action: PayloadAction<CompanyConfig['evaluationFrequency']>) => {
      state.config.evaluationFrequency = action.payload
      state.isModified = true
    },
    
    updatePerformanceWeight: (state, action: PayloadAction<{ 
      category: keyof CompanyConfig['performanceWeights']; 
      weight: number 
    }>) => {
      state.config.performanceWeights[action.payload.category] = action.payload.weight
      state.isModified = true
      delete state.errors[`weight_${action.payload.category}`]
    },
    
    updateIndustryType: (state, action: PayloadAction<CompanyConfig['industryType']>) => {
      state.config.industryType = action.payload
      state.isModified = true
    },
    
    updateTeamSize: (state, action: PayloadAction<CompanyConfig['teamSize']>) => {
      state.config.teamSize = action.payload
      state.isModified = true
    },
    
    toggleWorkingHoursFlexibility: (state) => {
      state.config.workingHoursFlexible = !state.config.workingHoursFlexible
      state.isModified = true
    },
    
    // 🎯 BULK OPERATIONS
    resetToDefaults: (state) => {
      state.config = { ...defaultConfig }
      state.isModified = true
      state.errors = {}
    },
    
    saveConfiguration: (state) => {
      // In a real app, this would trigger an API call
      state.isModified = false
      state.lastSaved = new Date().toISOString()
      state.errors = {}
    },
    
    setValidationErrors: (state, action: PayloadAction<Record<string, string>>) => {
      state.errors = action.payload
    },
    
    clearErrors: (state) => {
      state.errors = {}
    },
    
    // 🚀 LOAD: Load configuration (from API or localStorage)
    loadConfiguration: (state, action: PayloadAction<Partial<CompanyConfig>>) => {
      state.config = { ...state.config, ...action.payload }
      state.isModified = false
      state.lastSaved = new Date().toISOString()
      state.errors = {}
    }
  }
})

export const {
  updateOverallSpeed,
  updateMinimumScore,
  updateResponseTime,
  updateCommunicationStyle,
  toggleAsyncPriority,
  updateEvaluationFrequency,
  updatePerformanceWeight,
  updateIndustryType,
  updateTeamSize,
  toggleWorkingHoursFlexibility,
  resetToDefaults,
  saveConfiguration,
  setValidationErrors,
  clearErrors,
  loadConfiguration
} = configSlice.actions

export default configSlice.reducer

// 🎯 SELECTORS: Easy access to config state
export const selectConfig = (state: { config: ConfigState }) => state.config.config
export const selectIsModified = (state: { config: ConfigState }) => state.config.isModified
export const selectLastSaved = (state: { config: ConfigState }) => state.config.lastSaved
export const selectConfigErrors = (state: { config: ConfigState }) => state.config.errors

// 🚀 VALIDATORS: Configuration validation helpers
export const validateConfig = (config: CompanyConfig): Record<string, string> => {
  const errors: Record<string, string> = {}
  
  if (config.overallSpeedExpectation < 1 || config.overallSpeedExpectation > 10) {
    errors.overallSpeedExpectation = 'Speed expectation must be between 1 and 10'
  }
  
  if (config.minimumAcceptableScore < 0 || config.minimumAcceptableScore > 100) {
    errors.minimumAcceptableScore = 'Score must be between 0 and 100'
  }
  
  if (config.maxResponseTimeHours < 1 || config.maxResponseTimeHours > 168) { // Max 1 week
    errors.maxResponseTimeHours = 'Response time must be between 1 and 168 hours'
  }
  
  if (config.urgentResponseTimeMinutes < 5 || config.urgentResponseTimeMinutes > 1440) { // Max 1 day
    errors.urgentResponseTimeMinutes = 'Urgent response time must be between 5 and 1440 minutes'
  }
  
  // Validate performance weights sum to a reasonable total
  const totalWeight = Object.values(config.performanceWeights).reduce((sum, weight) => sum + weight, 0)
  if (totalWeight < 4 || totalWeight > 20) {
    errors.performanceWeights = 'Total performance weights should be between 4 and 20'
  }
  
  return errors
}
